import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useProgressOverlayContext } from "client/src/domain/EIF/QPSPushProgressOverlay/QPSPushProgressOverlay";
import { apiJobStatusToOverlayStatus } from "client/src/hooks/qps";
import { useSlobQuery } from "client/src/hooks/query";
import { useTrackQPSPushNotification } from "client/src/utils/analytics";
import { useEffect } from "react";
import type { UseQueryOptions } from "@tanstack/react-query";
import type { JsonToTypeMapper, ResponseError } from "client/src/hooks/query";
import type { APIJob } from "shared/types/APIJob";
import type { APIJobOutput } from "shared/types/APIJobOutput";
import type { ClientId } from "shared/types/Client";

export const jsonAPIJobToAPIJob: JsonToTypeMapper<APIJob> = (apiJob) => {
  return {
    ...apiJob,
    createdAt: new Date(apiJob.createdAt),
    updatedAt: new Date(apiJob.updatedAt),
    deletedAt: apiJob.deletedAt ? new Date(apiJob.deletedAt) : null,
    apiJobOutputs: apiJob.apiJobOutputs.map(jsonAPIJobOutputToAPIJobOutput),
  };
};

const jsonAPIJobOutputToAPIJobOutput: JsonToTypeMapper<APIJobOutput> = (apiJobOutput) => {
  return {
    ...apiJobOutput,
    createdAt: new Date(apiJobOutput.createdAt),
    updatedAt: new Date(apiJobOutput.updatedAt),
    deletedAt: apiJobOutput.deletedAt ? new Date(apiJobOutput.deletedAt) : null,
  };
};

export function useGetAPIJobs(
  clientIds: ClientId[],
  options?: Omit<UseQueryOptions<APIJob[], ResponseError>, "queryKey" | "queryFn">,
) {
  const query = useSlobQuery<APIJob[]>({
    method: "get",
    path: `/api/api-jobs?${clientIds.map((id) => `clientIds[]=${id}`).join("&")}`,
    map: (ec) => ec.map(jsonAPIJobToAPIJob),
    options,
  });

  return query;
}

export function usePollingAPIJobs(pollingInterval: number) {
  const queryClient = useQueryClient();
  const qpsProgressContext = useProgressOverlayContext();
  const qpsNotificationTracker = useTrackQPSPushNotification();

  const pollingClientIds = qpsProgressContext.cases.reduce<ClientId[]>((acc, clientCase) => {
    if (clientCase.status === "loading") {
      acc.push(clientCase.client.id);
    }
    return acc;
  }, []);

  const { data: trackedAPIJobs } = useGetAPIJobs(pollingClientIds, {
    enabled: pollingClientIds.length > 0,
    refetchInterval: pollingInterval,
  });

  useEffect(() => {
    if (trackedAPIJobs == null) {
      return;
    }

    for (const clientCase of qpsProgressContext.cases) {
      const apiJobs = trackedAPIJobs.filter((job) => job.clientId === clientCase.client.id);
      apiJobs.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      const [latestAPIJob] = apiJobs;
      if (latestAPIJob) {
        const status = apiJobStatusToOverlayStatus[latestAPIJob.status];
        if (status !== clientCase.status) {
          qpsProgressContext.updateStatus(clientCase.client.id, status);
          void queryClient.invalidateQueries({
            queryKey: ["get", `/api/clients/${clientCase.client.id}`],
          });
          void qpsNotificationTracker({
            apiJobStatus: latestAPIJob.status,
            clientId: clientCase.client.id,
            notificationType: "POLLING",
          });
          Sentry.captureException(new Error("QPS push notification updated by polling"));
        }
      }
    }
  }, [trackedAPIJobs, qpsProgressContext, queryClient, qpsNotificationTracker]);
}
